import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAdminOrSuperAdmin } from '../utils/auth';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import '../App.css';

interface Club {
  id: string;
  name: string;
  logo?: string;
  colors: { primary: string; secondary: string };
  membershipFee?: number;
  teamFee?: number;
  multiTeamDiscountType?: 'percentage' | 'fixed';
  multiTeamDiscountValue?: number;
  administratorId: string;
}

interface Team {
  id: string;
  name: string;
  teamNumber: string;
  teamFee?: number;
  leagueId?: string;
  league?: { id: string; name: string };
}

const ClubDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [club, setClub] = useState<Club | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);

  const canEditClub = club && user && (
    club.administratorId === user.id || isAdminOrSuperAdmin(user.profileType)
  );

  useEffect(() => {
    const fetchClubData = async () => {
      try {
        const [clubResponse, teamsResponse] = await Promise.all([
          api.get(`/clubs/${id}`),
          api.get(`/clubs/${id}/teams`)
        ]);

        setClub(clubResponse.data);
        setTeams(teamsResponse.data.teams || []);
      } catch (error) {
        console.error('Error fetching club data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchClubData();
    }
  }, [id]);

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!club) {
    return (
      <div className="container">
        <div className="card">
          <h2>Club not found</h2>
          <button className="btn btn-secondary" onClick={() => navigate('/teams')}>
            Back to Teams
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '20px' }}>
          {club.logo ? (
            <img src={club.logo} alt={club.name} style={{ width: '80px', height: '80px', borderRadius: '12px' }} />
          ) : (
            <div
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '12px',
                backgroundColor: club.colors.primary,
                border: `3px solid ${club.colors.secondary}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '24px',
                fontWeight: 'bold'
              }}
            >
              {club.name.charAt(0)}
            </div>
          )}
          <div style={{ flex: 1 }}>
            <h1>{club.name}</h1>
            {club.membershipFee != null && club.membershipFee > 0 && (
              <p style={{ color: '#757575', fontSize: '16px' }}>
                Membership Fee: ${club.membershipFee}
              </p>
            )}
          </div>
          {canEditClub && (
            <button
              className="btn btn-secondary"
              onClick={() => navigate(`/clubs/${club.id}/edit`)}
            >
              Edit Club
            </button>
          )}
        </div>

        {(club.teamFee != null || club.multiTeamDiscountType) && (
          <div style={{ padding: '15px', background: '#F5F5F5', borderRadius: '8px', marginBottom: '10px' }}>
            <h3 style={{ marginBottom: '10px' }}>Fee Information</h3>
            {club.teamFee != null && club.teamFee > 0 && (
              <p>Default Team Fee: <strong>${club.teamFee}</strong></p>
            )}
            {club.multiTeamDiscountType && club.multiTeamDiscountValue != null && (
              <p>
                Multi-Team Discount:{' '}
                <strong>
                  {club.multiTeamDiscountType === 'percentage'
                    ? `${club.multiTeamDiscountValue}%`
                    : `$${club.multiTeamDiscountValue}`
                  }
                </strong>
                {' '}off for additional teams
              </p>
            )}
          </div>
        )}
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2>Teams ({teams.length})</h2>
          {canEditClub && (
            <button
              className="btn btn-primary"
              onClick={() => navigate('/teams/create')}
              style={{ fontSize: '14px', padding: '8px 15px' }}
            >
              Create Team
            </button>
          )}
        </div>
        {teams.length === 0 ? (
          <p>No teams in this club yet.</p>
        ) : (
          <div className="grid">
            {teams.map((team) => (
              <div
                key={team.id}
                className="card"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/teams/${team.id}`)}
              >
                <h3>{team.name}</h3>
                <p style={{ color: '#757575', fontSize: '14px' }}>{team.teamNumber}</p>
                {team.teamFee != null && team.teamFee > 0 && (
                  <p style={{ fontSize: '14px' }}>Team Fee: ${team.teamFee}</p>
                )}
                {team.league && (
                  <p style={{ marginTop: '10px' }}>
                    <strong>League:</strong>{' '}
                    <Link
                      to={`/leagues?leagueId=${team.league.id}`}
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}
                    >
                      {team.league.name}
                    </Link>
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClubDetail;
